import { effect, inject, Injectable, signal } from '@angular/core';
import { ETipoPermiso } from '@core/enums/tipoPermiso.enum';
import { IPermisoGrupo } from '@core/interfaces/grupoPermiso.interface';
import { primeraLetraMayuscula } from '@shared/utils/functions.utils';
import { AppService } from './app.service';
import { SucursalService } from './sucursal.service';

@Injectable({
  providedIn: 'root',
})
export class GrupoPermisoService {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.generarPermisosAgrupados();
      }
    });
  }

  // servicios
  private appService = inject(AppService);
  private sucursalService = inject(SucursalService);
  private categoriaLabels: Record<string, string> = {
    ADMINISTRADOR: 'Administrador',
    VENDEDOR: 'Vendedor',
    CLIENTE: 'Clientes',
    PRODUCTO: 'Productos',
    CATEGORIA: 'Categorías',
    TIPO_PAGO: 'Tipos de pago',
    MOVIMIENTO_CAJA: 'Movimientos de caja',
    VENTA: 'Ventas',
    EMPLEADO: 'Empleados',
    CAJA: 'Caja',
    KARDEX: 'Kardex',
    COMANDAS: 'Comandas',
    GESTOR_TIEMPO: 'Gestor de tiempo',
    SUCURSAL: 'Sucursales',
    SESIONES: 'Sesiones',
  };

  private accionLabels: Record<string, string> = {
    CREAR: 'Crear',
    EDITAR: 'Editar',
    ELIMINAR: 'Eliminar',
    VER_LISTA: 'Ver lista',
    VER: 'Ver',
    REVOCAR: 'Revocar',
    CREAR_INGRESO: 'Crear ingreso',
    CREAR_SALIDA: 'Crear salida',
    OBTENER_REPORTE: 'Obtener reporte',
    VER_LISTA_PERMISOS: 'Ver lista de permisos',
    TURNO_OBTENER_REPORTE: 'Obtener reporte',
    A_CREDITO: 'Realizar venta a crédito',
    A_CREDITO_VER_LISTA: 'Ver lista de ventas a crédito',
    EDITAR_PRECIO_VENTA: 'Editar precio de venta',
    EMPRESA_EDITAR: 'Editar información de la empresa',

    STOCK_TRANSFERIR: 'Transferir stock entre sucursales',
    STOCK_INGRESAR_PRODUCTO: 'Ingresar producto a sucursal',
    ELIMINAR_PRODUCTOS: 'Eliminar o limpiar los productos',
  };

  private actionsCustomLabels: Record<string, string> = {
    EDITAR_PRECIO_VENTA: 'Editar precio de venta',
    EMPRESA_EDITAR: 'Editar información de la empresa',
  };

  // Método para obtener las categorías
  private getCategoriaLabels(): Record<string, string> {
    return this.categoriaLabels;
  }

  getNombrePermisoAmigable(permiso: string): string {
    const categoria = Object.keys(this.categoriaLabels).find((cat) => permiso.startsWith(cat));
    if (categoria) {
      const accion = permiso.slice(categoria.length + 1);
      return (
        this.accionLabels[accion] || accion.replaceAll('_', ' ') || primeraLetraMayuscula(permiso)
      );
    }
    return this.actionsCustomLabels[permiso] || permiso;
  }

  getNombreCategoriaAmigable(categoria: string): string {
    return this.categoriaLabels[categoria] || categoria;
  }

  getDescripcionGrupo(categoria: string): string {
    const descripciones: Record<string, string> = {
      ADMINISTRADOR: 'Acceso completo al sistema, incluyendo configuración y gestión total.',
      VENDEDOR: 'Permisos relacionados con la gestión de ventas y atención al cliente.',
      CLIENTE: 'Permisos para crear, editar y ver clientes.',
      PRODUCTO: 'Permisos para administrar productos, ingresos y salidas.',
      CATEGORIA: 'Gestión de categorías de productos.',
      TIPO_PAGO: 'Administración de tipos de pago.',
      MOVIMIENTO_CAJA: 'Control de movimientos en caja.',
      VENTA: 'Gestión completa de ventas.',
      EMPLEADO: 'Administración de empleados y sus permisos.',
      CAJA: 'Gestión de caja y reportes relacionados.',
      KARDEX: 'Gestión de Kardex y reportes relacionados.',
      COMANDAS: 'Gestión de comandas para restaurantes o servicios de comida.',
      GESTOR_TIEMPO: 'Gestión de tiempos y turnos para empleados.',
      SUCURSAL: 'Gestión de sucursales y su configuración.',
      SESIONES: 'Gestión de sesiones de usuario.',
    };
    return descripciones[categoria] || 'Descripción no disponible para esta categoría.';
  }
  permisosAgrupados = signal<IPermisoGrupo[]>([]);
  private permisosDisponibles: ETipoPermiso[] = Object.values(ETipoPermiso);
  private generarPermisosAgrupados() {
    const mapa = new Map<string, ETipoPermiso[]>();

    for (const permiso of this.permisosDisponibles) {
      const categoriaEncontrada = Object.keys(this.getCategoriaLabels()).find((cat) =>
        permiso.startsWith(cat),
      );

      const categoria = categoriaEncontrada || 'Otros';

      if (!mapa.has(categoria)) {
        mapa.set(categoria, []);
      }
      mapa.get(categoria)!.push(permiso);
    }

    this.permisosAgrupados.set(
      Array.from(mapa.entries())
        .sort(([categoriaA], [categoriaB]) => {
          if (categoriaA === 'Otros') return 1;
          if (categoriaB === 'Otros') return -1;
          return 0;
        })
        .map(([categoria, permisos]) => ({
          categoria,
          permisos,
        })),
    );
  }
}
